import * as React from "react";
import { formatCredits, formatCreditsUsd } from "@/lib/format";
import { CertaintyBadge } from "./certainty-badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export interface CostPreviewProps extends React.HTMLAttributes<HTMLDivElement> {
  estimateMin?: number; // credits
  estimateMax?: number; // credits
  cap: number; // credits
  currentBalance: number; // credits
  /** False while the balance has not been read or could not be: show unknown, not zero. */
  balanceKnown?: boolean;
  title?: string;
}

/**
 * CostPreview shows the financial commitments of a prospective run before start:
 * - Estimate range
 * - Cap marker
 * - Reserve amount and explanation
 * - Balance remaining after the reservation
 */
export function CostPreview({
  estimateMin,
  estimateMax,
  cap,
  currentBalance,
  balanceKnown = true,
  title = "Cost preview",
  className,
  ...props
}: CostPreviewProps) {
  const safeCap = Math.max(0, cap);
  const balanceAfter = currentBalance - safeCap;
  const isDeficit = balanceAfter < 0;

  const hasEstimate =
    typeof estimateMin === "number" &&
    typeof estimateMax === "number" &&
    estimateMax >= estimateMin;

  return (
    <Card className={cn("overflow-hidden", className)} {...props}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          <CertaintyBadge certainty="estimated" />
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        {hasEstimate ? (
          <div className="flex items-baseline justify-between border-b pb-2">
            <span className="text-muted-foreground">Estimated spend</span>
            <span className="font-medium tabular-nums text-foreground">
              {formatCredits(estimateMin)} to {formatCredits(estimateMax)} credits (
              {formatCreditsUsd(estimateMin)} to {formatCreditsUsd(estimateMax)})
            </span>
          </div>
        ) : null}

        <div className="flex items-baseline justify-between">
          <span className="text-muted-foreground">Spend cap</span>
          <span className="font-medium tabular-nums text-foreground">
            {formatCredits(safeCap)} credits ({formatCreditsUsd(safeCap)})
          </span>
        </div>

        <div className="flex items-baseline justify-between border-b pb-2">
          <span className="text-muted-foreground">Held on start</span>
          <div className="flex items-center gap-1.5 tabular-nums">
            <span className="font-semibold text-foreground">{formatCredits(safeCap)} credits</span>
            <CertaintyBadge certainty="reserved" />
          </div>
        </div>

        <div className="flex items-baseline justify-between font-medium">
          <span className="text-muted-foreground">Balance after reserve</span>
          <span
            className={cn(
              "tabular-nums",
              isDeficit ? "font-semibold text-danger" : "text-foreground",
            )}
          >
            {!balanceKnown
              ? "Unknown"
              : isDeficit
                ? `−${formatCredits(Math.abs(balanceAfter))} credits (${formatCreditsUsd(Math.abs(balanceAfter))} deficit)`
                : `${formatCredits(balanceAfter)} credits (${formatCreditsUsd(balanceAfter)})`}
          </span>
        </div>

        <p className="text-xs leading-relaxed text-muted-foreground">
          The cap is reserved when the run starts. Unused credits are released back to your balance
          upon completion or cancellation.
        </p>

        {isDeficit ? (
          <div
            role="alert"
            className="rounded-md border border-danger/30 bg-danger/10 p-2 text-xs text-danger"
          >
            This cap exceeds your available balance of {formatCredits(currentBalance)} credits. Top
            up or reduce the cap before starting.
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
